import { useState } from "react";
import { toast } from "sonner";
import { MessageSquare } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { countries, useLang, useT } from "../../lib/i18n";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const DIAL = Object.fromEntries("DE49 AT43 CH41 LI423 LU352 NL31 BE32 FR33 IT39 ES34 PT351 GB44 IE353 DK45 SE46 NO47 FI358 IS354 PL48 CZ420 SK421 HU36 SI386 HR385 RO40 BG359 GR30 CY357 MT356 EE372 LV371 LT370 RS381 BA387 ME382 MK389 AL355 TR90 UA380 MD373 US1 CA1 MX52 BR55 AR54 CL56 CO57 PE51 AU61 NZ64 ZA27 NG234 KE254 EG20 MA212 IN91 AE971 SA966 QA974 IL972 JP81 KR82 CN86 SG65 HK852 TH66 PH63 MY60 ID62 VN84"
  .split(" ").map((x) => [x.slice(0, 2), x.slice(2)]));

export function SmsActivation({ user, onDone }) {
  const t = useT();
  const lang = useLang();
  const names = Object.fromEntries(countries(lang));
  const [cc, setCc] = useState(DIAL[user.country] ? user.country : "DE");
  const [num, setNum] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const full = `+${DIAL[cc]}${num.replace(/\D/g, "").replace(/^0+/, "")}`;
  const valid = /^\+[1-9]\d{7,14}$/.test(full);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const send = () => run(async () => { await api.post("/me/phone", { phone: full, sms_marketing: consent }); setSent(true); toast.success(t("Code gesendet – gültig 10 Minuten", "Code sent – valid for 10 minutes")); });
  const verify = () => run(async () => { await api.post("/me/phone/verify", { code }); toast.success(t("SMS-Benachrichtigungen aktiv", "SMS notifications active")); onDone?.(); });
  return (
    <div className="space-y-4" data-testid="sms-activation">
      <p className="text-sm text-muted-foreground">{t("Gib deine Handynummer ein. Wir senden dir einen 6-stelligen Code per SMS.", "Enter your mobile number. We will send you a 6-digit code by SMS.")}</p>
      <div className="flex gap-2">
        <select value={cc} onChange={(e) => setCc(e.target.value)} className="h-10 w-36 rounded-md border border-input bg-secondary px-2 text-sm" data-testid="sms-dial-select">
          {Object.keys(DIAL).sort((a, b) => (names[a] || a).localeCompare(names[b] || b)).map((c) => <option key={c} value={c}>{names[c] || c} +{DIAL[c]}</option>)}
        </select>
        <Input value={num} onChange={(e) => setNum(e.target.value)} placeholder="151 2345678" inputMode="tel" data-testid="sms-phone-input" />
      </div>
      <p className={`font-num text-xs ${valid ? "text-[var(--win)]" : "text-muted-foreground"}`} data-testid="sms-phone-preview">{full}</p>
      <label className="flex items-start gap-3 text-xs text-muted-foreground"><Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="sms-marketing-consent" />{t("Optional: Ich möchte zusätzlich Angebote per SMS erhalten.", "Optional: I also want to receive offers by SMS.")}</label>
      <Button className="w-full rounded-full" disabled={!valid || busy} onClick={send} data-testid="sms-send-code">{sent ? t("Code erneut senden", "Resend code") : t("Code senden", "Send code")}</Button>
      {sent && (
        <div className="flex gap-2">
          <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} maxLength={6} placeholder="123456" inputMode="numeric" data-testid="sms-code-input" />
          <Button disabled={code.length !== 6 || busy} onClick={verify} data-testid="sms-verify-btn">{t("Bestätigen", "Verify")}</Button>
        </div>
      )}
    </div>
  );
}

export function SmsDialog({ open, onOpenChange, user, onDone }) {
  const t = useT();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="sms-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display text-2xl"><MessageSquare size={20} className="text-[var(--ball)]" />{t("SMS-Benachrichtigung aktivieren", "Activate SMS notifications")}</DialogTitle></DialogHeader>
        <SmsActivation user={user} onDone={() => { onOpenChange(false); onDone?.(); }} />
      </DialogContent>
    </Dialog>
  );
}
